/**
 * CR229: T8 - Template Labels Panel
 * CR313: Deal-type aware label definitions
 * CR450: Canonical component-scoped keys (value_box_title, etc.)
 * 
 * Third tab of the reorganized editor.
 * Controls label text overrides for various card sections.
 * Shows contextual field names based on deal type (e.g., "Points Earned" for POINTS).
 */

import { useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tag } from "lucide-react";
import type { CardTemplateLabels } from "@/providers/DealCardConfigProvider";
import { 
  DEFAULT_LABELS_BY_DEAL_TYPE, 
  CANONICAL_LABEL_KEYS,
  type CanonicalLabelKey,
  type DealTypeKey,
  normalizeDealType,
  resolveLabelFromRecord,
} from "@/lib/deal-cards/specV2/defaultLabels";

// ================================================================
// TYPES
// ================================================================

interface LabelFieldDefinition {
  key: CanonicalLabelKey;
  /** Display label in the form - contextual per deal type */
  formLabel: string;
  /** Placeholder text - the default value for this deal type */
  placeholder: string;
  /** Description of what this label controls */
  description: string;
  /** CR450: Show the backing canonical key */
  canonicalKey: string;
}

// ================================================================
// DEAL-TYPE AWARE LABEL DEFINITIONS
// ================================================================

/**
 * CR450: Generate label field definitions based on deal type using canonical keys.
 */
function getLabelDefinitionsForDealType(dealTypeKey: string): LabelFieldDefinition[] {
  const normalizedType = normalizeDealType(dealTypeKey);
  const defaults = DEFAULT_LABELS_BY_DEAL_TYPE[normalizedType];
  
  // Define form labels based on deal type semantics
  const formLabels: Record<DealTypeKey, Record<CanonicalLabelKey, string>> = {
    BONUS: {
      value_box_title: "Effective Value Title",
      reward_box_title: "Bonus Value Title",
      qualifying_spend_box_title: "Qualifying Spend Title",
      eligibility_box_title: "Eligibility Title",
    },
    POINTS: {
      value_box_title: "Cash Equivalent Title",
      reward_box_title: "Points Earned Title",
      qualifying_spend_box_title: "Qualifying Spend Title",
      eligibility_box_title: "Eligibility Title",
    },
    DISCOUNT: {
      value_box_title: "Effective Value Title",
      reward_box_title: "Discount Title",
      qualifying_spend_box_title: "Qualifying Spend Title",
      eligibility_box_title: "Eligibility Title",
    },
    IN_KIND: {
      value_box_title: "Estimated Value Title",
      reward_box_title: "Free Item Title",
      qualifying_spend_box_title: "Qualifying Spend Title",
      eligibility_box_title: "Eligibility Title",
    },
    UNCLASSIFIED: {
      value_box_title: "Value Title",
      reward_box_title: "Reward Title",
      qualifying_spend_box_title: "Qualifying Spend Title",
      eligibility_box_title: "Eligibility Title",
    },
  };
  
  const descriptions: Record<CanonicalLabelKey, string> = {
    value_box_title: "Title shown above the computed value display",
    reward_box_title: "Title shown above the reward/bonus amount",
    qualifying_spend_box_title: "Title shown above the spend requirement",
    eligibility_box_title: "Title shown above eligibility requirements",
  };
  
  const typeFormLabels = formLabels[normalizedType];
  
  return CANONICAL_LABEL_KEYS.map((key) => ({
    key,
    formLabel: typeFormLabels[key],
    placeholder: defaults[key],
    description: descriptions[key],
    canonicalKey: key,
  }));
}

// ================================================================
// COMPONENT
// ================================================================

interface TemplateLabelsPanelProps {
  labels: CardTemplateLabels;
  onChange: (key: string, value: string) => void;
  /** CR313: Deal type key for contextual labels */
  dealTypeKey?: string;
}

export function TemplateLabelsPanel({ 
  labels, 
  onChange, 
  dealTypeKey = "UNCLASSIFIED" 
}: TemplateLabelsPanelProps) {
  // CR450: Generate deal-type aware field definitions with canonical keys
  const labelDefinitions = useMemo(
    () => getLabelDefinitionsForDealType(dealTypeKey),
    [dealTypeKey]
  );
  
  // CR450: Resolve effective labels using canonical keys with legacy fallback
  const effectiveLabels = useMemo(() => {
    const result: Record<string, string> = {};
    const labelsRecord = labels as Record<string, string | undefined>;
    
    for (const key of CANONICAL_LABEL_KEYS) {
      result[key] = resolveLabelFromRecord(labelsRecord, key) || "";
    }
    
    return result;
  }, [labels]);
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Tag className="h-4 w-4" />
          Label Overrides
        </div>
        <Badge variant="outline" className="text-xs">
          {dealTypeKey}
        </Badge>
      </div>
      
      <p className="text-xs text-muted-foreground">
        Customize the labels displayed on various card sections. 
        Leave empty to use defaults shown in placeholders.
      </p>
      
      <div className="grid grid-cols-2 gap-4">
        {labelDefinitions.map((def) => (
          <div key={def.key} className="space-y-1.5">
            <div className="flex items-center gap-2">
              <Label className="text-xs text-muted-foreground">{def.formLabel}</Label>
              <span className="text-[9px] text-muted-foreground/50 font-mono">{def.canonicalKey}</span>
            </div>
            <Input
              value={effectiveLabels[def.key] || ""}
              onChange={(e) => onChange(def.key, e.target.value)}
              placeholder={def.placeholder}
              className="h-8 text-sm"
            />
            <p className="text-[10px] text-muted-foreground">{def.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
